/**
 * Curated language presets for the onboarding picker. Each carries the caption
 * direction, register phrase, and typography budgets the render/translate
 * stages consume. The operator picks one; it's stored verbatim on the channel.
 * Arabic is the default and keeps the exact founding-channel values.
 *
 * Latin budgets are larger than Arabic (Latin glyphs run narrower); they're
 * starting values tuned per language and adjustable later.
 */
export interface LanguagePreset {
  code: string;
  displayName: string;
  /** Flag emoji for the picker row — the one glance that finds a language in a
   *  list faster than reading it. A language is not a country; the flag is the
   *  conventional one for its script/register and nothing more. UI-only. */
  flag: string;
  /** The language's name IN that language, in its own script ("Français",
   *  "العربية"). Shown beside the English name in the picker so a speaker
   *  recognises their own language without translating ours. UI-only — the
   *  channel payload never carries it (`buildLanguagePayload`). */
  nativeName: string;
  direction: "rtl" | "ltr";
  register: string;
  budgets: { topLine: number; bottomLine: number; oneLine: number };
  glyphRatio: number;
  /** A short sample caption (top line + punch) for the live preview. UI-only —
   *  never persisted on the channel. */
  sample: { top: string; punch: string };
  /** In-language sample speaker name + role for the preview card. UI-only.
   *  Keyed per language so an RTL channel never shows an Arabic name on a
   *  Hebrew preview (and vice-versa). */
  sampleSpeaker: { name: string; role: string };
}

// The same felt line — "You won't get anything unless you ask" — rendered per
// language so the presets read as one system and the operator can compare them
// like-for-like. Sample text is preview-only; only the typographic fields below
// (direction/register/budgets/glyphRatio) ever reach the channel.
export const LANGUAGE_PRESETS: LanguagePreset[] = [
  {
    code: "ar", flag: "🇸🇦", displayName: "Arabic", nativeName: "العربية", direction: "rtl", register: "Modern Standard Arabic (Fusha)",
    budgets: { topLine: 22, bottomLine: 14, oneLine: 22 }, glyphRatio: 0.6,
    sample: { top: "لن تحقق شيئًا إن لم", punch: "تطلب ما تريد" },
    sampleSpeaker: { name: "نوفاك ديوكوفيتش", role: "لاعب تنس محترف" },
  },
  {
    code: "en", flag: "🇬🇧", displayName: "English", nativeName: "English", direction: "ltr", register: "standard English",
    budgets: { topLine: 34, bottomLine: 20, oneLine: 34 }, glyphRatio: 0.5,
    sample: { top: "You won't get anything", punch: "unless you ask" },
    sampleSpeaker: { name: "Novak Djokovic", role: "Pro tennis player" },
  },
  {
    code: "fr", flag: "🇫🇷", displayName: "French", nativeName: "Français", direction: "ltr", register: "standard French",
    budgets: { topLine: 32, bottomLine: 20, oneLine: 32 }, glyphRatio: 0.5,
    sample: { top: "Tu n'obtiendras rien si", punch: "tu ne demandes pas" },
    sampleSpeaker: { name: "Novak Djokovic", role: "Joueur de tennis pro" },
  },
  {
    code: "es", flag: "🇪🇸", displayName: "Spanish", nativeName: "Español", direction: "ltr", register: "standard Spanish",
    budgets: { topLine: 32, bottomLine: 20, oneLine: 32 }, glyphRatio: 0.5,
    sample: { top: "No lograrás nada si", punch: "no lo pides" },
    sampleSpeaker: { name: "Novak Djokovic", role: "Tenista profesional" },
  },
  {
    code: "de", flag: "🇩🇪", displayName: "German", nativeName: "Deutsch", direction: "ltr", register: "standard German",
    budgets: { topLine: 30, bottomLine: 18, oneLine: 30 }, glyphRatio: 0.52,
    sample: { top: "Du bekommst nichts, wenn", punch: "du nicht fragst" },
    sampleSpeaker: { name: "Novak Djokovic", role: "Profi-Tennisspieler" },
  },
  {
    code: "pt", flag: "🇵🇹", displayName: "Portuguese", nativeName: "Português", direction: "ltr", register: "standard Portuguese",
    budgets: { topLine: 32, bottomLine: 20, oneLine: 32 }, glyphRatio: 0.5,
    sample: { top: "Você não conseguirá nada", punch: "se não pedir" },
    sampleSpeaker: { name: "Novak Djokovic", role: "Tenista profissional" },
  },
  {
    code: "it", flag: "🇮🇹", displayName: "Italian", nativeName: "Italiano", direction: "ltr", register: "standard Italian",
    budgets: { topLine: 32, bottomLine: 20, oneLine: 32 }, glyphRatio: 0.5,
    sample: { top: "Non otterrai nulla se", punch: "non lo chiedi" },
    sampleSpeaker: { name: "Novak Djokovic", role: "Tennista professionista" },
  },
  {
    code: "nl", flag: "🇳🇱", displayName: "Dutch", nativeName: "Nederlands", direction: "ltr", register: "standard Dutch",
    budgets: { topLine: 32, bottomLine: 20, oneLine: 32 }, glyphRatio: 0.5,
    sample: { top: "Je krijgt niets als", punch: "je er niet om vraagt" },
    sampleSpeaker: { name: "Novak Djokovic", role: "Proftennisser" },
  },
  {
    code: "tr", flag: "🇹🇷", displayName: "Turkish", nativeName: "Türkçe", direction: "ltr", register: "standard Turkish",
    budgets: { topLine: 32, bottomLine: 20, oneLine: 32 }, glyphRatio: 0.5,
    sample: { top: "İstemezsen hiçbir şey", punch: "elde edemezsin" },
    sampleSpeaker: { name: "Novak Djokovic", role: "Profesyonel tenisçi" },
  },
  {
    code: "he", flag: "🇮🇱", displayName: "Hebrew", nativeName: "עברית", direction: "rtl", register: "modern Hebrew",
    budgets: { topLine: 24, bottomLine: 15, oneLine: 24 }, glyphRatio: 0.58,
    sample: { top: "לא תשיג כלום אם", punch: "לא תבקש" },
    sampleSpeaker: { name: "נובאק ג'וקוביץ'", role: "טניסאי מקצועי" },
  },
];

export const DEFAULT_LANGUAGE = LANGUAGE_PRESETS[0]; // Arabic

/** Language codes whose scripts read right-to-left. Used to SEED the custom
 *  language step's direction — the operator can override it, because no code
 *  list is exhaustive and dialect/macro-language codes vary. Covers Arabic,
 *  Hebrew, Persian, Urdu plus the common long-tail (Pashto, Sindhi, Uyghur,
 *  Yiddish, Dhivehi, Sorani/Central Kurdish, Aramaic, Syriac, N'Ko, Dari). */
export const RTL_CODES = new Set([
  "ar", "he", "fa", "ur",
  "ps", "sd", "ug", "yi", "dv", "ckb", "arc", "syr", "nqo", "prs",
]);

export function directionForCode(code: string): "rtl" | "ltr" {
  return RTL_CODES.has(code.trim().toLowerCase()) ? "rtl" : "ltr";
}

/** Common non-Latin scripts by language code, so the font nudge names the real
 *  writing system (e.g. "Japanese", "Cyrillic") instead of wrongly asserting
 *  "Latin". Not exhaustive — unmatched codes fall back to a neutral label. */
const SCRIPT_BY_CODE: Record<string, string> = {
  hi: "Devanagari", mr: "Devanagari", ne: "Devanagari", sa: "Devanagari",
  bn: "Bengali", pa: "Gurmukhi", gu: "Gujarati", ta: "Tamil", te: "Telugu",
  kn: "Kannada", ml: "Malayalam", si: "Sinhala",
  ja: "Japanese", zh: "Chinese", ko: "Korean",
  ru: "Cyrillic", uk: "Cyrillic", bg: "Cyrillic", sr: "Cyrillic", mk: "Cyrillic", be: "Cyrillic", kk: "Cyrillic",
  el: "Greek", th: "Thai", hy: "Armenian", ka: "Georgian", am: "Ethiopic", km: "Khmer", my: "Burmese",
};

/** Human name of the writing system a language uses — drives the "upload a font
 *  that covers this script" nudge in the font step. Falls back to a neutral
 *  label rather than asserting a script family it can't determine. */
export function scriptName(code: string, direction: "rtl" | "ltr"): string {
  const c = code.trim().toLowerCase();
  if (c === "ar") return "Arabic";
  if (c === "he") return "Hebrew";
  if (c === "fa" || c === "ur" || c === "ps" || c === "sd" || c === "prs") return "Perso-Arabic";
  if (SCRIPT_BY_CODE[c]) return SCRIPT_BY_CODE[c];
  if (direction === "rtl") return "right-to-left";
  return "Latin-script";
}

const REF_LTR = LANGUAGE_PRESETS.find((p) => p.code === "en") ?? LANGUAGE_PRESETS[1];
const REF_RTL = DEFAULT_LANGUAGE;

/**
 * Build a preset-shaped object for a language that isn't in the curated list.
 * Typography defaults follow `direction` (RTL → Arabic-ish budgets, else Latin).
 * Direction is operator-supplied — seeded from the code via `directionForCode`
 * but overridable in the UI, so any script (including RTL codes off the seed
 * list) renders correctly. The preview sample falls back to the closest
 * same-direction reference so the layout still reads while the operator dials in
 * colors/logo — the caller surfaces a "placeholder" note. UI-only; the persisted
 * shape ({code,displayName,direction,register,budgets,glyphRatio}) is unchanged.
 */
export function makeCustomLanguage(displayName: string, code: string, direction: "rtl" | "ltr"): LanguagePreset {
  const c = code.trim().toLowerCase();
  const ref = direction === "rtl" ? REF_RTL : REF_LTR;
  const name = displayName.trim() || "Custom";
  return {
    code: c || "xx",
    flag: "🌐",
    displayName: name,
    // The operator typed ONE name; we have no second one to show. Repeating
    // it as the native name would just print it twice in the picker.
    nativeName: name,
    direction,
    register: `standard ${name}`,
    budgets: direction === "rtl"
      ? { topLine: 22, bottomLine: 14, oneLine: 22 }
      : { topLine: 32, bottomLine: 20, oneLine: 32 },
    glyphRatio: direction === "rtl" ? 0.6 : 0.5,
    sample: ref.sample,
    sampleSpeaker: ref.sampleSpeaker,
  };
}
